import { toIsoDate } from './date-utils';

/**
 * Shared mapper utilities for converting option-modal form values
 * into quote detail structures. Used by both SummaryComponent and
 * InsuranceTypesComponent to avoid duplicated mapping logic.
 */

// ─── Producer code normalisation ──────────────────────────────────
/** Producer code 802 is the "house account" default assigned by the backend
 *  when no intermediary is selected. The client treats it as "no producer". */
const HOUSE_ACCOUNT_PRODUCER_CODE = '802';

/** Returns null when the producer code is the house-account default (802),
 *  so the UI behaves as if no producer has been selected. */
export function normalizeProducerCode(code: string | number | null | undefined): string | null {
    if (code == null) return null;
    return String(code) === HOUSE_ACCOUNT_PRODUCER_CODE ? null : String(code);
}

/** Converts a UI-normalised null producer code back to '802' for the backend.
 *  Use this in outgoing API payloads so the backend always receives a valid code. */
export function denormalizeProducerCode(code: string | number | null | undefined): string {
    if (code == null) return HOUSE_ACCOUNT_PRODUCER_CODE;
    return String(code);
}

// ─── Height / weight conversion ───────────────────────────────────

const KG_TO_LB = 2.20462;
const INCHES_TO_CM = 2.54;

/** Converts feet + inches (option-modal's height inputs) into centimeters. */
export function feetInchesToCm(feet: any, inches: any): number | null {
    const f = Number(feet);
    const i = Number(inches);
    const totalInches = (Number.isFinite(f) ? f : 0) * 12 + (Number.isFinite(i) ? i : 0);
    if (!totalInches || totalInches <= 0) return null;
    return Math.round(totalInches * INCHES_TO_CM * 100) / 100;
}

/** Splits centimeters (as stored on the person) back into feet + inches for the height inputs. */
export function cmToFeetInches(heightCm: any): { feet: number | null; inches: number | null } {
    const cm = Number(heightCm);
    if (!Number.isFinite(cm) || cm <= 0) return { feet: null, inches: null };
    const totalInches = cm / INCHES_TO_CM;
    const feet = Math.floor(totalInches / 12);
    const inches = Math.round(totalInches - feet * 12);
    return inches === 12 ? { feet: feet + 1, inches: 0 } : { feet, inches };
}

/** Converts a weight (in the given unit) into pounds, the canonical unit stored on the person. */
export function weightToLb(weight: any, unit: any): number | null {
    const w = Number(weight);
    if (!Number.isFinite(w) || w <= 0) return null;
    const lb = unit === 'kg' ? w * KG_TO_LB : w;
    return Math.round(lb * 100) / 100;
}

// ─── Primitive helpers ────────────────────────────────────────────

/** Parses a raw form value (string with commas or number) into a clean number. */
export function parseAmount(v: any): number {
    const n = Number(
        String(v ?? 0)
            .toString()
            .replace(/,/g, ''),
    );
    return Number.isFinite(n) ? n : 0;
}

/** Normalizes Spanish/English relation strings into canonical keys. */
export function normalizeRelation(rel: any): 'spouse' | 'child' | 'other' | null {
    if (rel == null) return null;
    const r = String(rel).toLowerCase();
    if (['conyuge', 'cónyuge', 'conyugue', 'spouse'].includes(r)) return 'spouse';
    if (['child', 'hijo', 'hija', 'hijx', 'niño', 'niña'].includes(r)) return 'child';
    if (['madre', 'padre', 'hermano', 'hermana', 'abuelo', 'abuela',
        'nieto', 'nieta', 'primo', 'prima', 'tio', 'tía', 'tia',
        'sobrino', 'sobrina', 'other'].includes(r)) return 'other';
    return null;
}

// ─── Coverage matching ────────────────────────────────────────────

/**
 * Matches a saved QuoteCoverageDto against an InsuranceTypeCoverageDto.
 * Uses a 3-step fallback: coverage.id → coverageId → name (case-insensitive).
 */
export function matchCoverage(saved: any, typeCov: any): boolean {
    // 1. Direct nested object match
    if (saved.coverage?.id != null && typeCov.id != null && saved.coverage.id === typeCov.id) {
        return true;
    }
    // 2. Match by coverageId
    if (saved.coverageId != null && typeCov.id != null) {
        return saved.coverageId === typeCov.id;
    }
    // 3. Fallback: match by name (case-insensitive)
    if (saved.name && typeCov.name) {
        return (saved.name as string).toLowerCase() === (typeCov.name as string).toLowerCase();
    }
    return false;
}

// ─── Plan resolution ──────────────────────────────────────────────

/** Resolves `selectedPlanId` based on `formulaId` and the selected plan name. */
export function resolveSelectedPlanId(option: any, planOption: string | null): number | null {
    if (!planOption) return null;
    if (option?.formulaId === 'EnfermedadesGraves') {
        return option.seriousIllnessPlans?.find((p: any) => p.name === planOption)?.id ?? null;
    }
    if (option?.formulaId === 'RespaldoColonial' || option?.formulaId === 'UltimosGastos') {
        return option.coveragePlans?.find((p: any) => p.name === planOption)?.id ?? null;
    }
    return null;
}

// ─── Coverage mapping ─────────────────────────────────────────────

/**
 * Builds the full coverages array from modal form values.
 * Handles primary, additional, and plan-based coverages (Respaldo/Ultimos/Enfermedades).
 *
 * @param option          The InsuranceType definition (with coverages, coveragePlans, seriousIllnessPlans).
 * @param formValue       Raw form values from the option modal.
 * @param existingCoverages Optional existing coverages to merge by matching (for edit flows).
 */
export function mapCoveragesFromForm(
    option: any,
    formValue: any,
    existingCoverages?: any[],
): any[] {
    const existing = [...(existingCoverages ?? [])];
    const result: any[] = [];

    const coveragesDef: any[] = option?.coverages ?? [];
    const primary = coveragesDef.find((c: any) =>
        (!!c?.isPrimary || !!c?.isRequired) &&
        !(typeof c?.insurePrimaryFraction === 'number' && c.insurePrimaryFraction > 0));

    // Primary coverage
    if (primary) {
        const amount = parseAmount(formValue?.mainCoverage);
        const match = existing.find((ec: any) => matchCoverage(ec, primary));
        if (match) {
            result.push({ ...match, amount });
        } else {
            result.push({
                coverage: primary,
                coverageId: primary?.id ?? null,
                coverageCode: primary?.coverageCode ?? null,
                name: primary?.name ?? null,
                description: primary?.description ?? null,
                amount,
            });
        }
    }

    // Additional coverages (exclude calculated)
    const additionals = coveragesDef.filter((c: any) => c !== primary && !c?.isCalculated);
    for (const cov of additionals) {
        const selKey = `selected_${cov.id ?? cov.name ?? ''}`;
        const amtKey = `amount_${cov.id ?? cov.name ?? ''}`;
        const selected = !!formValue?.[selKey];
        if (!selected) continue;
        const amount = parseAmount(formValue?.[amtKey]);
        const match = existing.find((ec: any) => matchCoverage(ec, cov));
        if (match) {
            result.push({ ...match, amount });
        } else {
            result.push({
                coverage: cov,
                coverageId: cov?.id ?? null,
                coverageCode: cov?.coverageCode ?? null,
                name: cov?.name ?? null,
                description: cov?.description ?? null,
                amount,
            });
        }
    }

    // Plan-based coverages: Respaldo Colonial / Ultimos Gastos
    if (option?.formulaId === 'RespaldoColonial' || option?.formulaId === 'UltimosGastos') {
        if (formValue?.planOption) {
            const selectedPlan = option.coveragePlans?.find((p: any) => p.name === formValue.planOption);
            if (selectedPlan?.items) {
                selectedPlan.items.forEach((item: any) => {
                    if (!result.some((c: any) => (c.coverage?.id ?? c.coverageId ?? c.id) === item.coverageId)) {
                        result.push({
                            coverage: { id: item.coverageId, name: item.coverageName, description: item.description },
                            coverageId: item.coverageId,
                            coverageCode: item.coverageCode ?? null,
                            name: item.coverageName,
                            description: item.description ?? null,
                            amount: item.sumInsured,
                        });
                    }
                });
            }
        }
    }

    // Plan-based coverages: Enfermedades Graves
    if (option?.formulaId === 'EnfermedadesGraves') {
        if (formValue?.planOption) {
            const selectedPlan = option.seriousIllnessPlans?.find((p: any) => p.name === formValue.planOption);
            if (selectedPlan?.coverages) {
                selectedPlan.coverages.forEach((cov: any, index: number) => {
                    const coverageText = `${cov.coverageCode ?? ''} ${cov.coverageName ?? ''}`.toLowerCase();
                    const isSurvivalCoverage =
                        coverageText.includes('superviv') ||
                        coverageText.includes('survival');
                    const isDiagnosisCoverage =
                        coverageText.includes('diagn') ||
                        coverageText.includes('diagnost');
                    const amount = isSurvivalCoverage
                        ? Number(selectedPlan.survivalPayout ?? 0)
                        : isDiagnosisCoverage || index === 0
                            ? Number(selectedPlan.diagnosisPayout ?? 0)
                            : Number(selectedPlan.survivalPayout ?? 0);
                    const existingPlanCoverage = result.find(
                        (c: any) => (c.coverage?.id ?? c.coverageId ?? c.id) === cov.coverageId
                    );

                    if (existingPlanCoverage) {
                        existingPlanCoverage.amount = amount;
                    } else {
                        result.push({
                            coverage: { id: cov.coverageId, name: cov.coverageName, description: cov.description },
                            coverageId: cov.coverageId,
                            coverageCode: cov.coverageCode ?? null,
                            name: cov.coverageName,
                            description: cov.description ?? null,
                            amount,
                        });
                    }
                });
            }
        }
    }

    // Post-process: recalculate amounts for fraction-based coverages
    // The fraction is a percentage of the total of the required coverages that aren't a fraction
    // themselves (e.g. Desmembramiento = 100% of Muerte, both required).
    const requiredTotal = result
        .filter((c: any) => {
            const covDef = coveragesDef.find((cd: any) =>
                (cd.id != null && cd.id === (c.coverageId ?? c.coverage?.id)) ||
                (cd.name && c.name && cd.name.toLowerCase() === c.name.toLowerCase())
            );
            const isFraction = typeof covDef?.insurePrimaryFraction === 'number' && covDef.insurePrimaryFraction > 0;
            return covDef && (covDef.isPrimary || covDef.isRequired) && !isFraction;
        })
        .reduce((sum: number, c: any) => sum + (Number(c.amount) || 0), 0);

    for (const c of result) {
        const covDef = coveragesDef.find((cd: any) =>
            (cd.id != null && cd.id === (c.coverageId ?? c.coverage?.id)) ||
            (cd.name && c.name && cd.name.toLowerCase() === c.name.toLowerCase())
        );
        if (covDef && typeof covDef.insurePrimaryFraction === 'number' && covDef.insurePrimaryFraction > 0) {
            c.amount = Math.round((covDef.insurePrimaryFraction / 100) * requiredTotal * 100) / 100;
        }
    }

    return result;
}

// ─── Insured people mapping ───────────────────────────────────────

/**
 * Maps primary, spouse, and children insured people from modal form values.
 * Preserves existing person data for edit flows via merge.
 *
 * @param formValue     Raw form values from the option modal.
 * @param existingIps   Optional existing insured people to merge against.
 */
export function mapInsuredPeopleFromForm(formValue: any, existingIps?: any[]): any[] {
    const ips = [...(existingIps ?? [])];
    const outIps: any[] = [];

    // Primary
    const primaryIp = ips.find((p: any) => !!p?.isPrimary) || ({} as any);
    const primaryPerson = { ...(primaryIp.person || {}) } as any;
    if (formValue?.birthDate != null) primaryPerson.birthDate = toIsoDate(formValue.birthDate);
    if (formValue?.primaryGender != null) primaryPerson.gender = formValue.primaryGender;
    if (formValue?.occupationCode != null) primaryPerson.occupation = String(formValue.occupationCode);
    const primaryHeightCm = feetInchesToCm(formValue?.heightFeet, formValue?.heightInches);
    if (primaryHeightCm != null) primaryPerson.heightCm = primaryHeightCm;
    const primaryWeightLb = weightToLb(formValue?.weight, formValue?.weightUnit);
    if (primaryWeightLb != null) primaryPerson.weightLb = primaryWeightLb;
    outIps.push({ ...primaryIp, isPrimary: true, isBeneficiary: true, isDependent: false, person: primaryPerson });

    // Spouse
    const existingSpouse = ips.find(
        (p: any) =>
            !p?.isPrimary &&
            normalizeRelation((p?.person as any)?.primaryInsuredRelation) === 'spouse',
    );
    const spouseIncluded = !!formValue?.spouseIncluded;
    if (spouseIncluded) {
        const person = { ...((existingSpouse?.person as any) || {}) } as any;
        if (formValue?.spouseBirthDate != null) person.birthDate = toIsoDate(formValue.spouseBirthDate);
        if (formValue?.spouseGender != null) person.gender = formValue.spouseGender;
        person.primaryInsuredRelation = person.primaryInsuredRelation ?? 'Conyuge';
        person.relationship = person.relationship ?? 'Conyuge';
        const spouseFlags = FAMILY_MEMBER_FLAGS;
        outIps.push({ ...(existingSpouse || {}), isPrimary: false, ...spouseFlags, person });
    }

    // Children
    const childrenEnabled = !!formValue?.childrenEnabled;
    if (childrenEnabled && Array.isArray(formValue?.children)) {
        const existingChildren = ips.filter(
            (p: any) =>
                !p?.isPrimary &&
                normalizeRelation((p?.person as any)?.primaryInsuredRelation) === 'child',
        );
        const mapped = formValue.children.map((c: any, idx: number) => {
            const match = existingChildren[idx] || ({} as any);
            const person = { ...((match.person as any) || {}) } as any;
            if (c?.birthDate != null) person.birthDate = toIsoDate(c.birthDate);
            if (c?.gender != null) person.gender = c.gender;
            person.primaryInsuredRelation = person.primaryInsuredRelation ?? 'Child';
            person.relationship = person.relationship ?? 'Child';
            const childFlags = FAMILY_MEMBER_FLAGS;
            return { ...match, isPrimary: false, ...childFlags, person };
        });
        outIps.push(...mapped);
    }

    // Others (extended family)
    const othersEnabled = !!formValue?.othersEnabled;
    if (othersEnabled && Array.isArray(formValue?.others)) {
        const existingOthers = ips.filter(
            (p: any) =>
                !p?.isPrimary &&
                normalizeRelation((p?.person as any)?.primaryInsuredRelation) === 'other',
        );
        const mapped = formValue.others.map((o: any, idx: number) => {
            const match = existingOthers[idx] || ({} as any);
            const person = { ...((match.person as any) || {}) } as any;
            if (o?.birthDate != null) person.birthDate = toIsoDate(o.birthDate);
            if (o?.gender != null) person.gender = o.gender;
            const rel = o?.relationship || 'Madre';
            person.primaryInsuredRelation = rel;
            person.relationship = rel;
            const otherFlags = FAMILY_MEMBER_FLAGS;
            return { ...match, isPrimary: false, ...otherFlags, person };
        });
        outIps.push(...mapped);
    }

    return outIps;
}

/** Family members added in the option modal are always dependents; policy beneficiaries are captured at checkout. */
const FAMILY_MEMBER_FLAGS = { isBeneficiary: false, isDependent: true } as const;

// ─── Answer mapping ───────────────────────────────────────────────

/**
 * Extracts `question_*` form keys into answer DTOs, preserving existing IDs.
 *
 * @param formValue       Raw form values from the option modal.
 * @param existingAnswers Optional existing answers to preserve IDs from.
 */
export function mapAnswersFromForm(formValue: any, existingAnswers?: any[]): any[] {
    const answersOut: any[] = [];
    for (const key of Object.keys(formValue ?? {})) {
        if (key.startsWith('question_')) {
            const qId = Number(key.replace('question_', ''));
            if (!isNaN(qId)) {
                const existingAnswer = (existingAnswers ?? []).find((a: any) => a.questionId === qId);
                answersOut.push({
                    id: existingAnswer?.id ?? null,
                    questionId: qId,
                    value: String(formValue[key] ?? ''),
                });
            }
        }
    }
    return answersOut;
}
